import { Loader2, Pause, Play, Trash2, Zap } from "lucide-react";

import { ApiError } from "@/api/client";
import { usd } from "@/components/GuardarRecetaDialog";
import { Button } from "@/components/ui/button";
import { type Receta, useAccionReceta, useRecetas, useRecetasEstado } from "@/hooks/useRecetas";

function cuando(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
}

/** Reportes programados (internamente "recetas"): reportes que gustaron, repetidos sin consultar a la IA. */
export function RecetasPage() {
  const estado = useRecetasEstado();
  const encendidas = estado.data?.enabled === true;
  const recetas = useRecetas(encendidas);
  const accion = useAccionReceta();

  if (estado.isPending) return <Loader2 className="size-5 animate-spin" />;
  if (!encendidas) {
    return (
      <div className="max-w-xl text-sm text-muted-foreground">
        Los reportes programados están apagados en este servidor. Un administrador los enciende con <code>RECETAS_ENABLED</code>.
      </div>
    );
  }

  const correr = (r: Receta, a: "pausar" | "reanudar" | "ejecutar" | "borrar") => {
    if (a === "borrar" && !window.confirm(`¿Borrar el reporte programado «${r.nombre}»? Sus archivos se conservan.`)) return;
    accion.mutate({ id: r.id, accion: a });
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Reportes programados</h1>
        <p className="text-sm text-muted-foreground">
          Reportes que gustaron en «Hablando con AROC», repetidos sin consultar a la IA. Se guardan desde la tarjeta del
          documento con «Guardar y programar».
        </p>
      </div>
      {accion.isError && (
        <div role="alert" className="text-sm text-destructive">
          {accion.error instanceof ApiError ? accion.error.message : "No se pudo."}
        </div>
      )}
      {(recetas.data ?? []).length === 0 && !recetas.isPending && (
        <div className="text-sm text-muted-foreground">Todavía no hay reportes programados.</div>
      )}
      <div className="space-y-3">
        {(recetas.data ?? []).map((r) => {
          const u = r.ultima_corrida;
          return (
            <div key={r.id} className="rounded-lg border bg-card p-4 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-medium">
                    {r.nombre} {r.pausada && <span className="ml-1 rounded bg-muted px-1.5 text-xs">pausada</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {r.formato === "xlsx" ? "Excel" : "Word"} · {r.periodicidad_texto} · a{" "}
                    {r.destino === "telegram" ? "Telegram" : "AROC"} · máx {r.max_corridas_dia}/día
                    {r.resumen_llm && " · con resumen"}
                  </div>
                  <div className="mt-1 text-xs">
                    Próxima: {r.pausada ? "—" : cuando(r.next_run_at)} · Última:{" "}
                    {u ? (
                      <>
                        {cuando(u.inicio)} {u.estado === "ok" ? "✓" : u.estado === "error" ? `✗ ${u.error ?? ""}` : "…"}{" "}
                        {u.descarga && (
                          <a className="underline" href={u.descarga}>
                            descargar
                          </a>
                        )}
                      </>
                    ) : (
                      "nunca"
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Costo última corrida: {usd(u?.costo_usd ?? (u ? 0 : null))} · plática original:{" "}
                    {usd(r.costo_platica_usd)} · armarla: {usd(r.costo_borrador_usd)}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => correr(r, "ejecutar")} disabled={accion.isPending}>
                    <Zap className="mr-1 size-4" /> Ejecutar ahora
                  </Button>
                  {r.pausada ? (
                    <Button size="sm" variant="outline" onClick={() => correr(r, "reanudar")}>
                      <Play className="mr-1 size-4" /> Reanudar
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => correr(r, "pausar")}>
                      <Pause className="mr-1 size-4" /> Pausar
                    </Button>
                  )}
                  <Button size="sm" variant="outline" aria-label={`Borrar ${r.nombre}`} onClick={() => correr(r, "borrar")}>
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
